import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { mirrorClinicalEvent } from "@/lib/clinical/eventPersistence";
import type { ImportedEventDraft, ImportPreview, ImportVisibility } from "@/lib/importers/contracts";

export interface PersistImportOptions {
  user: User;
  patientId: string;
  preview: ImportPreview;
  fileSha256: string;
  visibilityScope: ImportVisibility;
  includeExcluded?: boolean;
}

export interface PersistImportResult {
  batchId: string;
  imported: number;
  excluded: number;
  mirrored: number;
  dateOnly: number;
  duplicateFile: boolean;
}

function eventAtIso(event: ImportedEventDraft) {
  if (!event.occurredAt || !["exact", "minute"].includes(event.timePrecision)) return null;
  const parsed = new Date(event.occurredAt);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
}

function eventTitle(event: ImportedEventDraft) {
  const amount = event.doseAmount != null
    ? `${event.doseAmount}${event.doseUnit ? ` ${event.doseUnit}` : ""}`
    : "";
  if (event.eventType === "dose") return [event.rawName || "Dose importada", amount].filter(Boolean).join(" ");
  if (event.eventType === "substance_use") return [event.rawName || "Uso importado", amount].filter(Boolean).join(" ");
  if (event.eventType === "meal") return "Refeição importada";
  if (event.eventType === "note") return "Nota importada";
  if (event.eventType === "checkin") return "Check-in importado";
  if (event.eventType === "sleep") return "Sono importado";
  return "Evento importado";
}

function timelineType(event: ImportedEventDraft) {
  if (event.eventType === "dose") return "medication_ingestion" as const;
  if (event.eventType === "substance_use") return "substance_exposure" as const;
  if (event.eventType === "meal") return "meal" as const;
  if (event.eventType === "checkin") return "checkin" as const;
  if (event.eventType === "sleep") return "sleep_start" as const;
  return "clinical_note" as const;
}

export async function persistImportPreview(options: PersistImportOptions): Promise<PersistImportResult> {
  const { user, patientId, preview, fileSha256, visibilityScope, includeExcluded = false } = options;
  if (!user?.id) throw new Error("Sessão expirada.");
  if (!patientId) throw new Error("Selecione o perfil/paciente de destino.");
  if (!fileSha256) throw new Error("Não foi possível calcular a identidade do arquivo.");

  const client = supabase as any;
  const { data: existing, error: existingError } = await client
    .from("clinical_import_batches")
    .select("id, imported_items, excluded_items")
    .eq("owner_id", user.id)
    .eq("patient_id", patientId)
    .eq("file_sha256", fileSha256)
    .is("reverted_at", null)
    .maybeSingle();
  if (existingError) throw existingError;
  if (existing?.id) {
    return {
      batchId: existing.id,
      imported: existing.imported_items ?? 0,
      excluded: existing.excluded_items ?? 0,
      mirrored: 0,
      dateOnly: 0,
      duplicateFile: true,
    };
  }

  const selectedEvents = preview.events.filter((event) => includeExcluded || !event.excludedByDefault);
  const excluded = preview.events.length - selectedEvents.length;
  const { data: batch, error: batchError } = await client
    .from("clinical_import_batches")
    .insert({
      owner_id: user.id,
      patient_id: patientId,
      source_app: preview.sourceApp,
      source_file_name: preview.sourceFileName,
      file_sha256: fileSha256,
      visibility_scope: visibilityScope,
      status: "processing",
      total_items: preview.events.length,
      imported_items: 0,
      excluded_items: excluded,
      warning_count: preview.summary.warnings,
      metadata: {
        parser_summary: preview.summary,
        include_excluded: includeExcluded,
        timezone_for_minute_events: "America/Sao_Paulo",
      },
    })
    .select("id")
    .single();
  if (batchError) throw batchError;

  try {
    const rows = selectedEvents.map((event, index) => ({
      owner_id: user.id,
      patient_id: patientId,
      batch_id: batch.id,
      source_app: event.sourceApp,
      source_file_name: event.sourceFileName,
      source_record_id: event.sourceRecordId ?? `row-${event.sourceIndex ?? index}`,
      source_experience_id: event.sourceExperienceId ?? null,
      source_index: event.sourceIndex ?? index,
      event_type: event.eventType,
      event_at: eventAtIso(event),
      local_date: event.localDate ?? null,
      time_precision: event.timePrecision,
      raw_name: event.rawName ?? null,
      canonical_substance_key: event.canonicalSubstanceKey ?? null,
      dose_amount: event.doseAmount ?? null,
      dose_unit: event.doseUnit ?? null,
      route: event.route ?? null,
      note: event.note ?? null,
      confidence: event.confidence,
      warnings: event.warnings,
      source_payload: event.sourcePayload ?? {},
      excluded_by_default: event.excludedByDefault ?? false,
      visibility_scope: visibilityScope,
    }));

    const insertedRows: any[] = [];
    for (let offset = 0; offset < rows.length; offset += 200) {
      const { data, error } = await client
        .from("clinical_import_events")
        .insert(rows.slice(offset, offset + 200))
        .select("*");
      if (error) throw error;
      insertedRows.push(...(data ?? []));
    }

    let mirrored = 0;
    for (const row of insertedRows) {
      if (!row.event_at || !["exact", "minute"].includes(row.time_precision)) continue;
      await mirrorClinicalEvent(user, {
        patient_id: patientId,
        source_table: "clinical_import_events",
        source_record_id: row.id,
        event_at: row.event_at,
        created_at: row.created_at,
        event_type: timelineType({ eventType: row.event_type } as ImportedEventDraft),
        title: eventTitle({
          eventType: row.event_type,
          rawName: row.raw_name,
          doseAmount: row.dose_amount,
          doseUnit: row.dose_unit,
        } as ImportedEventDraft),
        description: row.note,
        data_origin: `import:${preview.sourceApp}`,
        entered_by_role: visibilityScope === "personal" ? "patient" : "clinician",
        confidence_level: row.confidence,
        metadata: {
          import_batch_id: batch.id,
          source_app: preview.sourceApp,
          source_file_name: preview.sourceFileName,
          time_precision: row.time_precision,
          raw_name: row.raw_name,
          dose_amount: row.dose_amount,
          dose_unit: row.dose_unit,
          route: row.route,
          warnings: row.warnings,
          visibility_scope: visibilityScope,
        },
      });
      mirrored += 1;
    }

    const completedAt = new Date().toISOString();
    const { error: completeError } = await client
      .from("clinical_import_batches")
      .update({
        status: "completed",
        imported_items: insertedRows.length,
        completed_at: completedAt,
        metadata: {
          parser_summary: preview.summary,
          include_excluded: includeExcluded,
          mirrored_events: mirrored,
          date_only_events: insertedRows.filter((row) => row.time_precision === "date_only").length,
          timezone_for_minute_events: "America/Sao_Paulo",
        },
      })
      .eq("id", batch.id)
      .eq("owner_id", user.id);
    if (completeError) throw completeError;

    return {
      batchId: batch.id,
      imported: insertedRows.length,
      excluded,
      mirrored,
      dateOnly: insertedRows.filter((row) => row.time_precision === "date_only").length,
      duplicateFile: false,
    };
  } catch (error: any) {
    await client
      .from("clinical_import_batches")
      .update({ status: "failed", metadata: { parser_summary: preview.summary, failure: error?.message ?? String(error) } })
      .eq("id", batch.id)
      .eq("owner_id", user.id);
    throw error;
  }
}

export async function revertImportBatch(user: User, batchId: string, reason = "Lote revertido pelo usuário") {
  if (!user?.id) throw new Error("Sessão expirada.");
  const client = supabase as any;
  const { data: batch, error: batchError } = await client
    .from("clinical_import_batches")
    .select("id, status")
    .eq("id", batchId)
    .eq("owner_id", user.id)
    .single();
  if (batchError) throw batchError;
  if (batch.status === "reverted") return { reverted: 0 };

  const { data: events = [], error: eventsError } = await client
    .from("clinical_import_events")
    .select("id")
    .eq("batch_id", batchId)
    .eq("owner_id", user.id)
    .is("deleted_at", null);
  if (eventsError) throw eventsError;
  const eventIds = events.map((event: any) => event.id);
  const revertedAt = new Date().toISOString();

  if (eventIds.length) {
    const { error: timelineError } = await client
      .from("patient_psylog_events")
      .update({ deleted_at: revertedAt, deleted_by: user.id, delete_reason: reason, dedupe_key: null })
      .eq("owner_id", user.id)
      .eq("source_table", "clinical_import_events")
      .in("source_record_id", eventIds)
      .is("deleted_at", null);
    if (timelineError) throw timelineError;

    const { error: eventError } = await client
      .from("clinical_import_events")
      .update({ deleted_at: revertedAt, deleted_by: user.id, delete_reason: reason })
      .eq("owner_id", user.id)
      .eq("batch_id", batchId)
      .is("deleted_at", null);
    if (eventError) throw eventError;
  }

  const { error: updateError } = await client
    .from("clinical_import_batches")
    .update({ status: "reverted", reverted_at: revertedAt, reverted_by: user.id, revert_reason: reason })
    .eq("id", batchId)
    .eq("owner_id", user.id);
  if (updateError) throw updateError;
  return { reverted: eventIds.length };
}
